import { Box3, Object3D, Scene, Vector3 } from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import {
  CameraInterface,
  Engine,
  Entity
} from "../engine/Engine";
import {
  type CameraPresetId,
  getCameraPresetFromBox,
} from "../engine/camera/CameraPresets";
import { lerp } from "../util/MathUtil";

const lerpc = 0.4;

export class SmoothOrbitController extends Entity {
  private _orbitControls: OrbitControls;
  private _orbitTarget = new Vector3();
  private _orbitPosition = new Vector3();
  private _orbitAlignPosition = false;
  private _orbitAlignTarget = false;

  private __orbitChangeFunctionInstance;
  private __orbitStartFunctionInstance;
  private _fitCameraInstance: any;
  constructor(orbitControl: OrbitControls) {
    super();
    this._orbitControls = orbitControl;
    this._enabled = false;
    this.__orbitChangeFunctionInstance = this.onOrbitChange.bind(this);
    this.__orbitStartFunctionInstance = this.onOrbitStart.bind(this);
    this._fitCameraInstance = this.fitCamera.bind(this);
  }

  setOrbitTarget(value: Vector3, force = false) {
    this._orbitTarget = value;
    this._orbitAlignTarget = true;
    this.engine!.dispatchCustomEvent("camera-start");
    if (force) {
      this._orbitControls?.target.copy(value);
      this._orbitAlignTarget = false;
      this._orbitControls.update();
    }
  }

  setOrbitPosition(value: Vector3, force = false) {
    this._orbitPosition = value;
    this._orbitAlignPosition = true;
    this.engine!.dispatchCustomEvent("camera-start");
    if (force) {
      this._orbitControls?.object?.position.copy(value);
      this._orbitAlignPosition = false;
      this._orbitControls.update();
    }
  }

  cancelAnimation() {
    this._orbitAlignPosition = false;
    this._orbitAlignTarget = false;
    this._orbitControls.update();
  }

  onOrbitChange() {
    this.engine!.dispatchCustomEvent("request-update");
  }

  onOrbitStart() {
    this._orbitAlignPosition = false;
    this._orbitAlignTarget = false;
    this.engine!.dispatchCustomEvent("camera-start");
    this.engine!.dispatchCustomEvent("request-update");
  }

  onOrbitEnd() {
    this.engine!.dispatchCustomEvent("request-update");
    this.engine!.dispatchCustomEvent("camera-end");
  }

  animate(dt: number, scene?: Scene, camera?: CameraInterface): void {
    if (!this._orbitControls) {
      this._orbitControls = this.engine!.activeCamera!.controller;
    }

    let wasAligningPosition = this._orbitAlignPosition;
    if (this._orbitAlignPosition) {
      const positionDist = this._orbitControls.object.position.distanceTo(
        this._orbitPosition,
      );

      this._orbitAlignPosition = positionDist > 0.001;
      const newPosition = lerp(
        this._orbitControls.object.position,
        this._orbitPosition,
        lerpc,
      ) as Vector3;

      this._orbitControls.object.position.copy(newPosition);
      this._orbitControls.object.lookAt(this._orbitControls.target);
    }

    let wasAligningTarget = this._orbitAlignTarget;
    if (this._orbitAlignTarget) {
      const targetDist = this._orbitControls.target.distanceTo(
        this._orbitTarget,
      );

      this._orbitAlignTarget = targetDist > 0.001;

      const newTarget = lerp(
        this._orbitControls.target,
        this._orbitTarget,
        lerpc,
      ) as Vector3;
      this._orbitControls?.target.copy(newTarget!);
    }

    
    if (
      ((!this._orbitAlignTarget && wasAligningTarget) ||
        (!this._orbitAlignPosition && wasAligningPosition))
    ) {
      this.engine!.dispatchCustomEvent("camera-end");
    }

    requestAnimationFrame(() => {
      Engine.activeEngine?.dispatchCustomEvent("camera-change", {
        detail: Engine.activeEngine?.activeCamera?.instance,
      });
    });
  }

  onEnabled(): void {
    this._orbitControls?.addEventListener(
      "start",
      this.__orbitStartFunctionInstance,
    );

    this._orbitControls?.addEventListener(
      "change",
      this.__orbitChangeFunctionInstance,
    );
  }

  onDisabled(): void {
    this._orbitControls?.removeEventListener(
      "change",
      this.__orbitChangeFunctionInstance,
    );
    this._orbitControls?.removeEventListener(
      "start",
      this.__orbitStartFunctionInstance,
    );
  }

  fitCamera(e: any) {
    const detail = e.detail;
    const obj: Object3D | undefined =
      detail?.object ?? detail ?? Engine.activeEngine?.activeScene?.children[0];
    const preset = detail?.preset as CameraPresetId | undefined;

    const box = obj ? new Box3().setFromObject(obj, true) : undefined;

    let newSettings: { position: Vector3; target: Vector3 };

    if (preset && obj && box) {
      const cam = Engine.activeEngine?.activeCamera?.instance;
      const fovDeg = (cam as any)?.fov ?? 25;
      const aspect = (cam as any)?.aspect ?? 1;
      newSettings = getCameraPresetFromBox(box, preset, fovDeg, aspect);
    } else {
      newSettings = Engine.activeEngine!.activeCamera?.fit(obj!)!;
    }

    if (box ) {
      const size = new Vector3();
      box.getSize(size);
      const extent = Math.max(size.x, size.y, size.z);
      this._orbitControls.maxDistance = Math.max(extent * 2 + 2, 0.1);
    }

    this.setOrbitTarget(newSettings.target);
    this.setOrbitPosition(newSettings.position);
  }

  onAdd(engine: Engine): void {
    this.enabled = true;
    engine.addEventListener("fit-camera", this._fitCameraInstance);
  }

  onRemove(engine: Engine): void {
    this.enabled = false;
    engine.removeEventListener("fit-camera", this._fitCameraInstance);
  }
}
